import React from 'react';
import { useTheme } from '@emotion/react';
import { Box, CircularProgress, IconButton, Tooltip, Typography } from '@mui/material';
import { DesktopWindowsOutlined, OpenInNew, PhoneIphoneOutlined } from '@mui/icons-material';
import { ReactComponent as CloseIcon } from "assets/close_icon.svg";
import ModalComponent from 'ui-components/ModalComponent';
import { navigateToDomain } from 'utils/index';

const WIDTHS = { desktop: '100%', mobile: '420px' };

// The marketing site, which renders the preview. Points at a local instance
// when one is configured, so the preview can be worked on without deploying.
const previewUrl = () => {
    const base = process.env.REACT_APP_MAIN_WEB_URL;
    return base ? `${base.replace(/\/$/, '')}/pricing?preview=1` : navigateToDomain('main', 'pricing?preview=1');
};

/**
 * The real pricing page, rendered from the draft.
 *
 * The card preview beside the fields answers "how does this card read"; this
 * answers "how does the page read", including the comparison table, the FAQs
 * and the mobile layout. The frame asks for the draft when it is ready, and
 * the draft is posted to it - nothing is saved to show a preview.
 */
const PagePreviewDialog = ({ open, handleClose, draft, buildPreview }) => {
    const theme = useTheme();
    const frameRef = React.useRef(null);
    const [device, setDevice] = React.useState('desktop');
    const [status, setStatus] = React.useState('loading');
    const [error, setError] = React.useState(null);

    React.useEffect(() => {
        if (!open) return undefined;
        setStatus('loading');
        setError(null);

        let cancelled = false;

        const send = async () => {
            try {
                const payload = await buildPreview();
                if (cancelled) return;
                frameRef.current?.contentWindow?.postMessage(
                    { type: 'backrr:pricing-preview', payload },
                    '*',
                );
                setStatus('ready');
            } catch (err) {
                if (cancelled) return;
                const details = err?.response?.data?.message?.errors || err?.response?.data?.errors;
                setError(Array.isArray(details) ? details[0] : (err?.response?.data?.message || 'Could not build the preview.'));
                setStatus('error');
            }
        };

        // The frame says when it has mounted; until then a post would land nowhere.
        const onMessage = (event) => {
            if (event.data?.type === 'backrr:pricing-preview-ready') send();
        };

        window.addEventListener('message', onMessage);
        return () => {
            cancelled = true;
            window.removeEventListener('message', onMessage);
        };
    }, [open, draft, buildPreview]);

    if (!open) return null;

    return (
        <ModalComponent open={open} onClose={handleClose} style={{ maxWidth: 1180, width: '96%' }}>
            <Box sx={{ background: theme.palette.customColors.whiteBG, borderRadius: '8px', overflow: 'hidden' }}>
                <Box display="flex" alignItems="center" justifyContent="space-between" gap={1} padding="0.875rem 1.25rem"
                    sx={{ borderBottom: `1px solid ${theme.palette.customColors.b50}` }}>
                    <Box>
                        <Typography sx={{ fontWeight: 700, color: theme.palette.customColors.g300 }}>
                            Preview of the pricing page
                        </Typography>
                        <Typography sx={{ fontSize: '0.8125rem', color: theme.palette.customColors.g200 }}>
                            Your unpublished draft, rendered by the real page. Nothing here is live yet.
                        </Typography>
                    </Box>

                    <Box display="flex" alignItems="center" gap={0.5}>
                        <Tooltip title="Desktop">
                            <IconButton onClick={() => setDevice('desktop')}
                                sx={{ color: device === 'desktop' ? theme.palette.customColors.indigo700 : theme.palette.customColors.g75 }}>
                                <DesktopWindowsOutlined />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Mobile">
                            <IconButton onClick={() => setDevice('mobile')}
                                sx={{ color: device === 'mobile' ? theme.palette.customColors.indigo700 : theme.palette.customColors.g75 }}>
                                <PhoneIphoneOutlined />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Open the live page in a tab">
                            <IconButton onClick={() => window.open(navigateToDomain('main', 'pricing'), '_blank')}
                                sx={{ color: theme.palette.customColors.g75 }}>
                                <OpenInNew />
                            </IconButton>
                        </Tooltip>
                        <IconButton onClick={handleClose} sx={{ padding: 0, marginLeft: '0.5rem' }}>
                            <CloseIcon width={36} height={36} />
                        </IconButton>
                    </Box>
                </Box>

                <Box sx={{ position: 'relative', backgroundColor: theme.palette.customColors.yellow_bg, height: '72dvh' }}>
                    {status === 'loading' && (
                        <Box sx={{
                            position: 'absolute', inset: 0, display: 'grid', placeContent: 'center', zIndex: 1,
                            backgroundColor: 'rgba(255,255,255,0.7)',
                        }}>
                            <CircularProgress size={'1.5rem'} />
                        </Box>
                    )}

                    {status === 'error' && (
                        <Box sx={{ position: 'absolute', inset: 0, display: 'grid', placeContent: 'center', padding: '2rem', zIndex: 2, backgroundColor: theme.palette.customColors.whiteBG }}>
                            <Typography sx={{ fontWeight: 700, color: theme.palette.customColors.r500, textAlign: 'center' }}>
                                {error}
                            </Typography>
                            <Typography sx={{ fontSize: '0.8125rem', color: theme.palette.customColors.g200, textAlign: 'center', marginTop: 0.5 }}>
                                Fix it in the editor and open the preview again.
                            </Typography>
                        </Box>
                    )}

                    <Box sx={{ height: '100%', display: 'grid', placeItems: 'start center' }}>
                        <iframe
                            ref={frameRef}
                            title="Pricing page preview"
                            src={previewUrl()}
                            style={{
                                width: WIDTHS[device],
                                maxWidth: '100%',
                                height: '100%',
                                border: 'none',
                                transition: 'width 0.2s ease',
                            }}
                        />
                    </Box>
                </Box>
            </Box>
        </ModalComponent>
    );
};

export default PagePreviewDialog;
